import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type User } from './api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh(): Promise<void> {
    try {
      const data = await api.get<{ user: User }>('/auth/me');
      setUser(data.user);
    } catch {
      // A 401 here is the normal not-signed-in case, not an error worth surfacing.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function login(email: string, password: string, totpCode?: string): Promise<void> {
    const data = await api.post<{ user: User }>('/auth/login', { email, password, totpCode });
    setUser(data.user);
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      // Clear locally even if the request failed — the user asked to sign out
      // and leaving them looking signed in would be worse.
      setUser(null);
    }
  }

  /**
   * Permission check for conditional UI.
   *
   * This hides controls the user cannot use; it is NOT the security boundary.
   * Every one of these is enforced again on the server, which is what actually
   * matters.
   */
  function can(permission: string): boolean {
    return user?.permissions.includes(permission) ?? false;
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/**
 * Permission checks for one resource's page. The user's permissions say what
 * kinds of thing they may do somewhere; `level` (from the resource's own
 * payload: view | use | edit | full) says what they may do to this one. As
 * with can(), this only hides controls - the server enforces it.
 */
const NEEDS: Record<string, number> = {
  'resource:read': 1, 'recording:read': 1,
  'resource:start': 2, 'resource:stop': 2, 'resource:reboot': 2, 'resource:connect': 2, 'resource:credentials': 2, 'resource:sso': 2,
  'resource:edit': 3, 'recording:delete': 3,
  'resource:terminate': 4,
};
export function scopedCan(can: (p: string) => boolean, level: string | null | undefined) {
  const rank = ['view', 'use', 'edit', 'full'].indexOf(level ?? '') + 1;
  return (permission: string): boolean => can(permission) && (NEEDS[permission] === undefined || rank >= NEEDS[permission]);
}

export { ApiError };
